// DataBoard — generic Kanban board: a horizontally scrollable track of
// content-height column panels, each a surface with a tone dot, title and
// count pill. Display-only (no drag & drop). This owns the outer layer only —
// track, columns, headers, states; what renders inside a card is
// module-specific via renderCard (a single slot: the card shell carries the
// padding). The caller owns data fetching and grouping comes from columnKey;
// items whose columnKey matches no column are dropped. Unlike DataCards the
// track is hidden while a board-level state shows, so empty column panels
// never render underneath the status.

import type { CSSProperties, ReactNode } from 'react';

export type DataBoardTone =
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'purple'
  | 'brand'
  | 'neutral';

export interface DataBoardColumn {
  key: string;
  /** Already-translated column title. */
  label: ReactNode;
  /** Header dot tone → status token (--success, --warning, …). Default 'neutral'. */
  tone?: DataBoardTone;
  /** Override the derived count (e.g. server total while items hold one page). */
  count?: number;
}

export interface DataBoardProps<T> {
  columns: DataBoardColumn[];
  items: T[];
  itemKey: (item: T) => string;
  /** Column key an item belongs to (compared against DataBoardColumn.key). */
  columnKey: (item: T) => string;
  /** Fills a card — the whole card is one slot. */
  renderCard: (item: T) => ReactNode;
  onItemClick?: (item: T) => void;
  /** Extra class(es) on an item shell (e.g. a page-level selected modifier). */
  itemClassName?: (item: T) => string;
  /** Column width (px) — the track scrolls when columns overflow. Default 340. */
  columnWidth?: number;
  /** Shown inside a column with zero items (already translated). */
  emptyColumnText?: string;
  loading?: boolean;
  error?: boolean;
  /** Custom empty-state node (icon + text). */
  empty?: ReactNode;
  /** Fallback texts for loading/error states (already translated). */
  loadingText?: string;
  errorText?: string;
}

const DataBoard = <T,>({
  columns,
  items,
  itemKey,
  columnKey,
  renderCard,
  onItemClick,
  itemClassName,
  columnWidth = 340,
  emptyColumnText,
  loading = false,
  error = false,
  empty,
  loadingText,
  errorText,
}: DataBoardProps<T>) => {
  const rootStyle = { '--db-col': `${columnWidth}px` } as CSSProperties;

  const grouped = new Map<string, T[]>();
  for (const item of items) {
    const key = columnKey(item);
    const bucket = grouped.get(key);
    if (bucket) bucket.push(item);
    else grouped.set(key, [item]);
  }

  return (
    <div className='data-board' style={rootStyle}>
      {!loading && !error && items.length > 0 && (
        <div className='data-board__track'>
          {columns.map((col) => {
            const colItems = grouped.get(col.key) ?? [];
            return (
              <section key={col.key} className='data-board__col'>
                <header className='data-board__col-header'>
                  <span
                    className={`data-board__dot data-board__dot--${col.tone ?? 'neutral'}`}
                    aria-hidden='true'
                  />
                  <span className='data-board__col-title'>{col.label}</span>
                  <span className='data-board__count'>{col.count ?? colItems.length}</span>
                </header>
                <div className='data-board__cards'>
                  {colItems.map((item) => {
                    const extra = itemClassName?.(item);
                    return (
                      <div
                        key={itemKey(item)}
                        className={`data-board__item${
                          onItemClick ? ' data-board__item--clickable' : ''
                        }${extra ? ` ${extra}` : ''}`}
                        {...(onItemClick
                          ? {
                              role: 'button',
                              tabIndex: 0,
                              onClick: () => onItemClick(item),
                              onKeyDown: (e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onItemClick(item);
                                }
                              },
                            }
                          : {})}
                      >
                        {renderCard(item)}
                      </div>
                    );
                  })}
                  {colItems.length === 0 && emptyColumnText && (
                    <div className='data-board__col-empty'>{emptyColumnText}</div>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {loading ? (
        <div className='data-board__status'>{loadingText}</div>
      ) : error ? (
        <div className='data-board__status'>{errorText}</div>
      ) : items.length === 0 ? (
        <div className='data-board__status'>{empty}</div>
      ) : null}
    </div>
  );
};

export default DataBoard;
